import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Globe,
  Flame,
  Gem,
  AlertTriangle,
  ShieldCheck,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Star,
  Activity,
  BarChart2,
  Sparkles,
  ArrowUpRight,
  Info,
} from 'lucide-react';
import { NewsTrafficCrossSignal, TrafficSignalType } from '../types';

interface TrafficCrossSignalRadarProps {
  market: 'US' | 'KR' | 'ALL';
  lang: string;
  holdingSymbols: Set<string>;
  watchlistSymbols: Set<string>;
  onAddWatchlist?: (symbol: string, name?: string, market?: string, notes?: string) => void;
  onSelectNewsTitle?: (newsTitle: string) => void;
}

const SIGNAL_TYPE_META: Record<
  TrafficSignalType,
  {
    icon: any;
    badgeKo: string;
    badgeEn: string;
    descKo: string;
    descEn: string;
    formulaKo: string;
    formulaEn: string;
    borderClass: string;
    bgClass: string;
    textClass: string;
    pillClass: string;
  }
> = {
  CONFIRMED_BREAKOUT: {
    icon: Flame,
    badgeKo: '🔥 팩트검증 강세',
    badgeEn: '🔥 Confirmed Breakout',
    descKo: '호재 속보 + 실제 웹 트래픽 급증 일치 (강력 매수/추세 돌파)',
    descEn: 'Bullish Breaking News + Verified Web Traffic Surge',
    formulaKo: '판정 조건: 호재 뉴스 발생 + 주간 트래픽(WoW) ≥ +6.5% 또는 Z-Score ≥ +0.55σ',
    formulaEn: 'Criteria: Bullish News + Traffic WoW ≥ +6.5% or Z-Score ≥ +0.55σ',
    borderClass: 'border-emerald-500/30 hover:border-emerald-500/50',
    bgClass: 'bg-emerald-500/[0.04]',
    textClass: 'text-emerald-400',
    pillClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  },
  STEALTH_ACCUMULATION: {
    icon: Gem,
    badgeKo: '💎 스텔스 선취매',
    badgeEn: '💎 Stealth Surge',
    descKo: '언론 보도 전 웹 트래픽 이상 급증 포착 (어닝 서프라이즈 선점)',
    descEn: 'Pre-News Organic Web Traffic Anomaly (Early Alpha)',
    formulaKo: '판정 조건: 언론 보도 무풍지대 + 주간 트래픽(WoW) ≥ +2.0% 및 Z-Score ≥ +0.40σ',
    formulaEn: 'Criteria: Quiet Media + Traffic WoW ≥ +2.0% & Z-Score ≥ +0.40σ',
    borderClass: 'border-cyan-500/30 hover:border-cyan-500/50',
    bgClass: 'bg-cyan-500/[0.04]',
    textClass: 'text-cyan-400',
    pillClass: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  },
  HYPE_TRAP_WARNING: {
    icon: AlertTriangle,
    badgeKo: '⚠️ 과열·설거지 경고',
    badgeEn: '⚠️ Hype Trap Alert',
    descKo: '자극적 호재 뉴스 대비 실제 웹 트래픽 정체·하락 (추격매수 주의)',
    descEn: 'Bullish News Hype vs. Sluggish Real Web Traffic',
    formulaKo: '판정 조건: 자극적 호재 속보 연발 + 실제 웹 트래픽 역성장 (WoW ≤ -4.0% 또는 Z ≤ -0.40σ)',
    formulaEn: 'Criteria: Bullish Hype News + Weak Real Traffic (WoW ≤ -4.0% or Z ≤ -0.40σ)',
    borderClass: 'border-amber-500/30 hover:border-amber-500/50',
    bgClass: 'bg-amber-500/[0.04]',
    textClass: 'text-amber-400',
    pillClass: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  },
  OVERSOLD_CONTRARIAN: {
    icon: ShieldCheck,
    badgeKo: '🛡️ 과매도 반등포착',
    badgeEn: '🛡️ Oversold Contrarian',
    descKo: '단기 악재 속보에도 핵심 서비스 트래픽 견조 (저점 분할매수 기회)',
    descEn: 'Negative Headline Noise vs. Resilient Core Web Traffic',
    formulaKo: '판정 조건: 단기 악재 보도 + 핵심 서비스 트래픽 견조 유지 (이탈률 4% 미만 & 펀더멘털 정상)',
    formulaEn: 'Criteria: Negative News Noise + Resilient Core User Traffic (Low Churn & Strong Fundamentals)',
    borderClass: 'border-purple-500/30 hover:border-purple-500/50',
    bgClass: 'bg-purple-500/[0.04]',
    textClass: 'text-purple-400',
    pillClass: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  },
};

const ACTION_BADGE_STYLES: Record<string, string> = {
  BUY: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  ADD: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  WATCH: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  REDUCE: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  SELL: 'bg-red-500/20 text-red-300 border-red-500/40',
  HOLD: 'bg-white/10 text-white/60 border-white/15',
};

export function TrafficCrossSignalRadar({
  market,
  lang,
  holdingSymbols,
  watchlistSymbols,
  onAddWatchlist,
  onSelectNewsTitle,
}: TrafficCrossSignalRadarProps) {
  // Global master cache across market switching (prevents slow network reloads)
  const masterSignalsRef = useRef<NewsTrafficCrossSignal[]>([]);
  const [allSignals, setAllSignals] = useState<NewsTrafficCrossSignal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSignalFilter, setActiveSignalFilter] = useState<'ALL' | TrafficSignalType>('ALL');
  const [selectedSignal, setSelectedSignal] = useState<NewsTrafficCrossSignal | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  // Fetch full data (ALL) once to enable 0ms instantaneous market switching
  const fetchSignals = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && masterSignalsRef.current.length > 0) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/traffic/cross-signals?market=ALL${forceRefresh ? '&refresh=true' : ''}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { signals?: NewsTrafficCrossSignal[]; updatedAt?: string };
        const list: NewsTrafficCrossSignal[] = Array.isArray(data.signals) ? data.signals : [];
        masterSignalsRef.current = list;
        setAllSignals(list);
        setUpdatedAt(data.updatedAt || null);
        if (list.length > 0 && !selectedSignal) {
          setSelectedSignal(list[0]);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load web traffic cross signals');
      } finally {
        setIsLoading(false);
      }
    },
    [selectedSignal]
  );

  useEffect(() => {
    fetchSignals(false);
  }, [fetchSignals]);

  // Instantaneous market and signal type filtering in 0ms (no spinner or network delay)
  const filteredSignals = useMemo(() => {
    let list = allSignals;
    if (market !== 'ALL') {
      list = list.filter(s => s.market === market);
    }
    if (activeSignalFilter !== 'ALL') {
      list = list.filter(s => s.signalType === activeSignalFilter);
    }
    return list;
  }, [allSignals, market, activeSignalFilter]);

  const marketScopedSignals = useMemo(() => {
    if (market === 'ALL') return allSignals;
    return allSignals.filter(s => s.market === market);
  }, [allSignals, market]);

  const counts = useMemo(() => {
    return {
      ALL: marketScopedSignals.length,
      CONFIRMED_BREAKOUT: marketScopedSignals.filter(s => s.signalType === 'CONFIRMED_BREAKOUT').length,
      STEALTH_ACCUMULATION: marketScopedSignals.filter(s => s.signalType === 'STEALTH_ACCUMULATION').length,
      HYPE_TRAP_WARNING: marketScopedSignals.filter(s => s.signalType === 'HYPE_TRAP_WARNING').length,
      OVERSOLD_CONTRARIAN: marketScopedSignals.filter(s => s.signalType === 'OVERSOLD_CONTRARIAN').length,
    };
  }, [marketScopedSignals]);

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4 overflow-hidden">
      {/* Top Banner & Methodology Callout */}
      <div className="glass-premium rounded-2xl border border-white/10 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-start md:items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/25 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-black text-white">
                {lang === 'ko'
                  ? '웹 트래픽 대체데이터 × 증시 속보 교차 시그널 레이더'
                  : 'Web Traffic Alternative Data × Breaking News Cross-Signal Radar'}
              </h3>
              <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                NOWCASTING ALPHA
              </span>
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-white/50">
                {market === 'ALL' ? 'US & KR' : market} ({filteredSignals.length} Active)
              </span>
            </div>
            <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">
              {lang === 'ko'
                ? '기관 퀀트형 대체 데이터(Wikimedia 글로벌 일별 트래픽 시계열 · 테크 커뮤니티 버즈 · 도메인 민감도)와 실시간 증시 속보를 교차 검증하여 실적 서프라이즈 및 주가 영향도를 예측합니다.'
                : 'Cross-validates real-time breaking news with public web traffic alternative data (Wikimedia daily views, tech buzz, domain sensitivity) to forecast earnings & price impact.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end md:self-center flex-shrink-0">
          {updatedAt && (
            <span className="text-[10px] text-white/25 font-mono">
              {new Date(updatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => fetchSignals(true)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs font-bold border border-white/10 transition-all disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-cyan-400' : ''}`} />
            {lang === 'ko' ? '트래픽 재산출' : 'Recalculate'}
          </button>
        </div>
      </div>

      {/* 4-Matrix Filter Tabs with Rich Informative Tooltips */}
      <div className="flex flex-wrap gap-2 flex-shrink-0 relative">
        {/* ALL Tab */}
        <div className="relative">
          <button
            onClick={() => setActiveSignalFilter('ALL')}
            onMouseEnter={() => setActiveTooltip('ALL')}
            onMouseLeave={() => setActiveTooltip(null)}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all border flex items-center gap-1.5 ${
              activeSignalFilter === 'ALL'
                ? 'bg-white text-black border-white shadow-md'
                : 'bg-white/[0.03] text-white/50 border-white/10 hover:text-white'
            }`}
          >
            <span>{lang === 'ko' ? '전체 교차 시그널' : 'All Signals'}</span>
            <span className="text-[10px] opacity-75">({counts.ALL})</span>
          </button>
          {activeTooltip === 'ALL' && (
            <div className="absolute top-full left-0 mt-2 z-50 w-64 p-3 rounded-xl bg-[#0f141c] border border-white/15 shadow-2xl text-[11px] text-white/80 leading-relaxed pointer-events-none">
              <div className="font-bold text-white mb-1">
                {lang === 'ko' ? '전체 종목 종합 보기' : 'All Cross-Signals'}
              </div>
              {lang === 'ko'
                ? '미국 및 한국의 웹 트래픽-실적 민감도가 높은 대표 기업의 트래픽 모멘텀과 속보 교차 분석 전체 목록입니다.'
                : 'Complete list of all tracked US & KR companies combining daily web traffic trends and real-time news impact.'}
            </div>
          )}
        </div>

        {/* 4 Matrix Tabs */}
        {(Object.keys(SIGNAL_TYPE_META) as TrafficSignalType[]).map(type => {
          const meta = SIGNAL_TYPE_META[type];
          const isActive = activeSignalFilter === type;
          const isTooltipOpen = activeTooltip === type;

          return (
            <div key={type} className="relative">
              <button
                onClick={() => setActiveSignalFilter(type)}
                onMouseEnter={() => setActiveTooltip(type)}
                onMouseLeave={() => setActiveTooltip(null)}
                className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all border flex items-center gap-1.5 ${
                  isActive
                    ? `${meta.pillClass} shadow-md ring-1 ring-white/20`
                    : 'bg-white/[0.02] text-white/45 border-white/5 hover:bg-white/[0.05] hover:text-white/80'
                }`}
              >
                <span>{lang === 'ko' ? meta.badgeKo : meta.badgeEn}</span>
                <span className="text-[10px] opacity-75">({counts[type]})</span>
                <Info className="w-3 h-3 opacity-40 ml-0.5" />
              </button>

              {/* Rich Tooltip Popover */}
              {isTooltipOpen && (
                <div className="absolute top-full left-0 mt-2 z-50 w-72 p-3.5 rounded-xl bg-[#0d121a] border border-cyan-500/30 shadow-2xl text-[11px] leading-relaxed pointer-events-none">
                  <div className="flex items-center gap-1.5 font-black text-white text-xs mb-1.5">
                    <span className={meta.textClass}>{lang === 'ko' ? meta.badgeKo : meta.badgeEn}</span>
                  </div>
                  <p className="text-white/90 font-medium mb-2">
                    {lang === 'ko' ? meta.descKo : meta.descEn}
                  </p>
                  <div className="p-2 rounded-lg bg-white/5 border border-white/5 text-[10px] text-cyan-300 font-mono">
                    {lang === 'ko' ? meta.formulaKo : meta.formulaEn}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
        {isLoading && allSignals.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 py-4">
            {[1, 2, 3, 4, 5, 6].map(n => (
              <div
                key={n}
                className="h-56 rounded-2xl bg-white/[0.02] border border-white/5 animate-pulse p-5 flex flex-col justify-between"
              >
                <div className="h-5 w-1/2 bg-white/10 rounded" />
                <div className="h-16 w-full bg-white/5 rounded-xl" />
                <div className="h-8 w-full bg-white/10 rounded-lg" />
              </div>
            ))}
          </div>
        ) : error && allSignals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
            <p className="text-xs text-white/50">{error}</p>
            <button
              onClick={() => fetchSignals(true)}
              className="px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-300 text-xs font-bold border border-cyan-500/30"
            >
              {lang === 'ko' ? '다시 시도' : 'Retry'}
            </button>
          </div>
        ) : filteredSignals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2 text-white/30">
            <Globe className="w-8 h-8 opacity-40" />
            <p className="text-xs font-bold">
              {lang === 'ko'
                ? '선택한 조건에 해당하는 웹 트래픽 교차 시그널이 없습니다.'
                : 'No cross signals found for the selected filter.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-6">
            {filteredSignals.map(sig => {
              const meta = SIGNAL_TYPE_META[sig.signalType] || SIGNAL_TYPE_META.STEALTH_ACCUMULATION;
              const isHeld = holdingSymbols.has(sig.symbol.toUpperCase());
              const isWatched = watchlistSymbols.has(sig.symbol.toUpperCase());
              const isPositiveImpact = sig.predictedImpactPct >= 0;
              const series = sig.historySeries || [];
              const trafficValues = series.map(pt => pt.trafficIndex);
              const maxTraffic = Math.max(1, ...trafficValues);
              const minTraffic = Math.min(...trafficValues);

              return (
                <div
                  key={sig.id}
                  onClick={() => setSelectedSignal(sig)}
                  className={`rounded-2xl border p-4 transition-all cursor-pointer flex flex-col justify-between gap-3.5 ${meta.bgClass} ${meta.borderClass} ${
                    selectedSignal?.id === sig.id ? 'ring-1 ring-white/30 shadow-xl' : ''
                  }`}
                >
                  {/* Card Top: Signal Badge & Action */}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <span
                        className={`text-[10px] font-black px-2.5 py-0.5 rounded-full border ${meta.pillClass}`}
                      >
                        {lang === 'ko' ? meta.badgeKo : meta.badgeEn}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {isHeld && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            {lang === 'ko' ? '보유' : 'HELD'}
                          </span>
                        )}
                        {isWatched && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            {lang === 'ko' ? '관심' : 'WATCH'}
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                            ACTION_BADGE_STYLES[sig.recommendedAction] || ACTION_BADGE_STYLES.WATCH
                          }`}
                        >
                          {sig.recommendedAction}
                        </span>
                      </div>
                    </div>

                    {/* Company & Domain Header */}
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-base font-black font-mono text-white">{sig.symbol}</span>
                          <span className="text-xs font-bold text-white/80 truncate">
                            {lang === 'ko' ? sig.companyName : sig.companyNameEn || sig.companyName}
                          </span>
                          <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-white/5 text-white/40">
                            {sig.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Globe className="w-3 h-3 text-cyan-400/70" />
                          <span className="text-[11px] font-mono text-cyan-300/90">{sig.primaryDomain}</span>
                          {sig.sector && (
                            <span className="text-[10px] text-white/30 truncate">· {sig.sector}</span>
                          )}
                        </div>
                      </div>

                      {/* Predicted Price Impact */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-[9px] text-white/35 font-bold uppercase">
                          {lang === 'ko' ? '예상 주가영향' : 'Est. Impact'}
                        </div>
                        <div
                          className={`text-sm font-black font-mono flex items-center justify-end gap-0.5 ${
                            isPositiveImpact ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {isPositiveImpact ? (
                            <TrendingUp className="w-3.5 h-3.5" />
                          ) : (
                            <TrendingDown className="w-3.5 h-3.5" />
                          )}
                          <span>
                            {isPositiveImpact ? '+' : ''}
                            {sig.predictedImpactPct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3-Column Metrics Strip */}
                  <div className="grid grid-cols-3 gap-2 p-2.5 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <div className="text-[9px] text-white/35 font-semibold">
                        {lang === 'ko' ? '주간 트래픽(WoW)' : 'Traffic WoW'}
                      </div>
                      <div
                        className={`text-xs font-black font-mono mt-0.5 ${
                          sig.trafficWowGrowthPct >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {sig.trafficWowGrowthPct >= 0 ? '+' : ''}
                        {sig.trafficWowGrowthPct.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/35 font-semibold">
                        {lang === 'ko' ? '이상치(Z-Score)' : 'Anomaly Z'}
                      </div>
                      <div
                        className={`text-xs font-black font-mono mt-0.5 ${
                          sig.trafficZScore >= 0.8
                            ? 'text-cyan-400'
                            : sig.trafficZScore < 0
                            ? 'text-amber-400'
                            : 'text-white/80'
                        }`}
                      >
                        {sig.trafficZScore >= 0 ? '+' : ''}
                        {sig.trafficZScore.toFixed(2)}σ
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/35 font-semibold">
                        {lang === 'ko' ? '트래픽 모멘텀' : 'Momentum'}
                      </div>
                      <div className="text-xs font-black font-mono text-white mt-0.5">
                        {sig.trafficMomentumScore.toFixed(0)}
                        <span className="text-[9px] text-white/30">/100</span>
                      </div>
                    </div>
                  </div>

                  {/* 14-Day High-Visibility Bar Chart */}
                  <div className="space-y-1.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5 text-white/60 font-bold">
                        <BarChart2 className="w-3.5 h-3.5 text-cyan-400" />
                        <span>{lang === 'ko' ? '14일 웹 유입 추이' : '14-Day Traffic Trend'}</span>
                      </div>
                      <span className="text-[9px] font-mono text-white/35">
                        {series[0]?.date ? `${series[0].date.slice(5)} ~ ${series[series.length - 1]?.date.slice(5)}` : ''}
                      </span>
                    </div>

                    {/* Chart Bars Container with explicit pixel heights */}
                    <div className="h-12 w-full flex items-end justify-between gap-1 pt-1.5 border-b border-white/10 pb-1">
                      {series.map((pt, i) => {
                        const range = Math.max(1, maxTraffic - minTraffic);
                        const normalizedHeight = Math.max(18, Math.round(((pt.trafficIndex - minTraffic) / range) * 80 + 20));
                        const isRecent3Days = i >= series.length - 3;

                        return (
                          <div
                            key={pt.date || i}
                            className="flex-1 flex flex-col items-center justify-end h-full group/bar relative"
                          >
                            {/* Bar Tooltip on Hover */}
                            <div className="absolute bottom-full mb-1 z-30 hidden group-hover/bar:flex flex-col items-center pointer-events-none">
                              <div className="bg-[#111622] text-white border border-cyan-500/40 px-2 py-1 rounded text-[9px] font-mono whitespace-nowrap shadow-xl">
                                <div>{pt.date}</div>
                                <div className="text-cyan-300 font-bold">
                                  {pt.trafficIndex.toLocaleString()} views
                                </div>
                              </div>
                            </div>

                            {/* Bar Element */}
                            <div
                              style={{ height: `${normalizedHeight}%` }}
                              className={`w-full rounded-t-sm transition-all duration-300 ${
                                isRecent3Days
                                  ? sig.trafficWowGrowthPct >= 0
                                    ? 'bg-gradient-to-t from-emerald-600 to-emerald-400 shadow-sm shadow-emerald-500/20'
                                    : 'bg-gradient-to-t from-amber-600 to-amber-400 shadow-sm shadow-amber-500/20'
                                  : 'bg-white/20 hover:bg-white/40'
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between text-[9px] text-white/30 font-mono">
                      <span>D-14</span>
                      <span>D-7</span>
                      <span className={sig.trafficWowGrowthPct >= 0 ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>
                        Today ({sig.trafficWowGrowthPct >= 0 ? '+' : ''}{sig.trafficWowGrowthPct.toFixed(0)}%)
                      </span>
                    </div>
                  </div>

                  {/* Linked Breaking News Box (if present) */}
                  {sig.newsTitle ? (
                    <div className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[9px] font-black uppercase tracking-wider text-purple-300 flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" />
                          {lang === 'ko' ? '결합된 실시간 증시 속보' : 'Matched Breaking News'}
                        </span>
                        {sig.newsUrl && (
                          <a
                            href={sig.newsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-[9px] text-white/40 hover:text-white flex items-center gap-0.5"
                          >
                            <span>{lang === 'ko' ? '원문' : 'Link'}</span>
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                      <p
                        onClick={e => {
                          if (onSelectNewsTitle && sig.newsTitle) {
                            e.stopPropagation();
                            onSelectNewsTitle(sig.newsTitle);
                          }
                        }}
                        className="text-[11px] font-semibold text-white/85 line-clamp-1 hover:text-cyan-300 transition-colors"
                      >
                        {sig.newsTitle}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 rounded-xl bg-cyan-500/[0.05] border border-cyan-500/15 flex items-center justify-between">
                      <span className="text-[10px] font-bold text-cyan-300/90">
                        {lang === 'ko'
                          ? '🤫 언론 보도 전 트래픽 선행 시그널 감지'
                          : '🤫 Pre-news organic traffic anomaly detected'}
                      </span>
                    </div>
                  )}

                  {/* AI Rationale */}
                  <p className="text-[11px] text-white/65 leading-relaxed line-clamp-3">
                    {lang === 'ko' ? sig.aiRationaleKo : sig.aiRationaleEn}
                  </p>

                  {/* Footer Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <a
                      href={`https://${sig.primaryDomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-[10px] font-bold text-white/40 hover:text-white flex items-center gap-1"
                    >
                      <span>{sig.primaryDomain}</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </a>

                    {!isHeld && !isWatched && onAddWatchlist && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onAddWatchlist(
                            sig.symbol,
                            sig.companyName,
                            sig.market,
                            lang === 'ko' ? sig.aiRationaleKo : sig.aiRationaleEn
                          );
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/15 text-white/70 hover:text-white text-[10px] font-bold border border-white/10 transition-all"
                      >
                        <Star className="w-3 h-3 text-amber-400" />
                        {lang === 'ko' ? '관심종목 추가' : 'Add Watchlist'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
